/**
 * Redux slice pour la gestion de l'historique des notes
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
    NoteHistoryEntry,
    VersionComparison,
    getNoteHistory,
    getNoteVersion,
    compareVersions,
    restoreVersion
} from './noteHistoryAPI';
import { LOADING_STATES } from '../../constants';

// Types pour le state
export interface NoteHistoryState {
    // Historique de la note actuellement consultée
    currentNoteId: string | null;
    entries: NoteHistoryEntry[];
    
    // Version sélectionnée pour aperçu
    selectedVersion: NoteHistoryEntry | null;
    
    // Comparaison entre deux versions
    comparison: VersionComparison | null;
    comparisonVersions: { versionA: number; versionB: number } | null;
    
    // Panel d'historique ouvert/fermé
    isPanelOpen: boolean;
    
    // États de chargement
    status: typeof LOADING_STATES[keyof typeof LOADING_STATES];
    comparisonStatus: typeof LOADING_STATES[keyof typeof LOADING_STATES];
    restoreStatus: typeof LOADING_STATES[keyof typeof LOADING_STATES];
    
    // Erreurs
    error: string | null;
}

// État initial
const initialState: NoteHistoryState = {
    currentNoteId: null,
    entries: [],
    selectedVersion: null,
    comparison: null,
    comparisonVersions: null,
    isPanelOpen: false,
    status: LOADING_STATES.IDLE,
    comparisonStatus: LOADING_STATES.IDLE,
    restoreStatus: LOADING_STATES.IDLE,
    error: null
};

// Async thunks

/**
 * Charge l'historique d'une note
 */
export const fetchNoteHistory = createAsyncThunk(
    'noteHistory/fetchHistory',
    async ({ noteId, limit = 50 }: { noteId: string; limit?: number }, { rejectWithValue }) => {
        try {
            const entries = await getNoteHistory(noteId, limit);
            return { noteId, entries };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur lors du chargement de l\'historique');
        }
    }
);

/**
 * Charge une version spécifique
 */
export const fetchNoteVersion = createAsyncThunk(
    'noteHistory/fetchVersion',
    async ({ noteId, version }: { noteId: string; version: number }, { rejectWithValue }) => {
        try {
            return await getNoteVersion(noteId, version);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur lors du chargement de la version');
        }
    }
);

/**
 * Compare deux versions
 */
export const fetchVersionComparison = createAsyncThunk(
    'noteHistory/compareVersions',
    async (
        { noteId, versionA, versionB }: { noteId: string; versionA: number; versionB: number },
        { rejectWithValue }
    ) => {
        try {
            const comparison = await compareVersions(noteId, versionA, versionB);
            return { comparison, versionA, versionB };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur lors de la comparaison');
        }
    }
);

/**
 * Restaure une version
 */
export const restoreNoteVersion = createAsyncThunk(
    'noteHistory/restoreVersion',
    async (
        { noteId, version, authorName }: { noteId: string; version: number; authorName: string },
        { rejectWithValue, dispatch }
    ) => {
        try {
            const result = await restoreVersion(noteId, version, authorName);
            // Recharger l'historique après restauration
            dispatch(fetchNoteHistory({ noteId }));
            return result;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur lors de la restauration');
        }
    }
);

// Slice
const noteHistorySlice = createSlice({
    name: 'noteHistory',
    initialState,
    reducers: {
        // Ouvre/ferme le panel d'historique
        toggleHistoryPanel(state) {
            state.isPanelOpen = !state.isPanelOpen;
        },
        
        openHistoryPanel(state) {
            state.isPanelOpen = true;
        },
        
        closeHistoryPanel(state) {
            state.isPanelOpen = false;
            state.selectedVersion = null;
            state.comparison = null;
            state.comparisonVersions = null;
        },
        
        // Sélectionne une version pour aperçu
        selectVersion(state, action: PayloadAction<NoteHistoryEntry | null>) {
            state.selectedVersion = action.payload;
            // Reset la comparaison si on sélectionne une version unique
            if (action.payload) {
                state.comparison = null;
                state.comparisonVersions = null;
            }
        },
        
        // Reset l'état
        resetHistoryState(state) {
            return initialState;
        },
        
        // Clear les erreurs
        clearError(state) {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // fetchNoteHistory
        builder.addCase(fetchNoteHistory.pending, (state) => {
            state.status = LOADING_STATES.LOADING;
            state.error = null;
        });
        builder.addCase(fetchNoteHistory.fulfilled, (state, action) => {
            state.status = LOADING_STATES.SUCCEEDED;
            state.currentNoteId = action.payload.noteId;
            state.entries = action.payload.entries;
        });
        builder.addCase(fetchNoteHistory.rejected, (state, action) => {
            state.status = LOADING_STATES.FAILED;
            state.error = action.payload as string;
        });
        
        // fetchNoteVersion
        builder.addCase(fetchNoteVersion.pending, (state) => {
            state.status = LOADING_STATES.LOADING;
        });
        builder.addCase(fetchNoteVersion.fulfilled, (state, action) => {
            state.status = LOADING_STATES.SUCCEEDED;
            state.selectedVersion = action.payload;
        });
        builder.addCase(fetchNoteVersion.rejected, (state, action) => {
            state.status = LOADING_STATES.FAILED;
            state.error = action.payload as string;
        });
        
        // fetchVersionComparison
        builder.addCase(fetchVersionComparison.pending, (state) => {
            state.comparisonStatus = LOADING_STATES.LOADING;
        });
        builder.addCase(fetchVersionComparison.fulfilled, (state, action) => {
            state.comparisonStatus = LOADING_STATES.SUCCEEDED;
            state.comparison = action.payload.comparison;
            state.comparisonVersions = {
                versionA: action.payload.versionA,
                versionB: action.payload.versionB
            };
        });
        builder.addCase(fetchVersionComparison.rejected, (state, action) => {
            state.comparisonStatus = LOADING_STATES.FAILED;
            state.error = action.payload as string;
        });
        
        // restoreNoteVersion
        builder.addCase(restoreNoteVersion.pending, (state) => {
            state.restoreStatus = LOADING_STATES.LOADING;
        });
        builder.addCase(restoreNoteVersion.fulfilled, (state) => {
            state.restoreStatus = LOADING_STATES.SUCCEEDED;
            state.selectedVersion = null;
        });
        builder.addCase(restoreNoteVersion.rejected, (state, action) => {
            state.restoreStatus = LOADING_STATES.FAILED;
            state.error = action.payload as string;
        });
    }
});

// Actions
export const {
    toggleHistoryPanel,
    openHistoryPanel,
    closeHistoryPanel,
    selectVersion,
    resetHistoryState,
    clearError
} = noteHistorySlice.actions;

// Selectors
export const selectHistoryEntries = (state: { noteHistory: NoteHistoryState }) =>
    state.noteHistory.entries;

export const selectSelectedVersion = (state: { noteHistory: NoteHistoryState }) =>
    state.noteHistory.selectedVersion;

export const selectComparison = (state: { noteHistory: NoteHistoryState }) =>
    state.noteHistory.comparison;

export const selectIsHistoryPanelOpen = (state: { noteHistory: NoteHistoryState }) =>
    state.noteHistory.isPanelOpen;

export const selectHistoryStatus = (state: { noteHistory: NoteHistoryState }) =>
    state.noteHistory.status;

export const selectHistoryError = (state: { noteHistory: NoteHistoryState }) =>
    state.noteHistory.error;

// Reducer
export default noteHistorySlice.reducer;
